// src/components/report/LongTermProjection.js
import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0 && payload[0] && payload[1] && payload[2]) {
        return (
            <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-lg">
                <p className="font-bold mb-2">سنة {label}</p>
                <p className="text-sm">
                    القيمة المستثمرة: 
                    <span className="font-bold mr-2 ltr inline-block">
                        EGP {payload[0].value.toLocaleString()}
                    </span>
                </p>
                <p className="text-sm">
                    القيمة المتوقعة: 
                    <span className="font-bold mr-2 ltr inline-block">
                        EGP {payload[1].value.toLocaleString()}
                    </span>
                </p>
                <p className="text-sm">
                    كمية الدهب: 
                    <span className="font-bold mr-2 ltr inline-block">
                        {payload[2].value.toFixed(3)} g
                    </span>
                </p>
            </div>
        );
    }
    return null;
};

const LongTermProjection = ({ reportData }) => {
    if (!reportData?.performance_metrics?.investment_summary?.average_monthly_investment) {
        return null;
    }

    const monthlyAverage = reportData.performance_metrics.investment_summary.average_monthly_investment;
    const currentPrice = reportData.purchase_metrics?.current_gold_price_per_gram || 4354;

    // Generate 10-year projection data
    const generateProjectionData = () => {
        const yearlyInvestment = monthlyAverage * 12;
        const projectedData = [];
        let totalInvestment = 0;
        let goldAccumulated = 0;
        let currentMonthly = monthlyAverage;
    
        for (let year = 1; year <= 10; year++) {
            // 10% increase in monthly savings each year
            currentMonthly = year === 1 ? monthlyAverage : currentMonthly * 1.1;
            const yearlyAmount = currentMonthly * 12;
            totalInvestment += yearlyAmount;
            
            // 10% yearly gold price increase
            const projectedGoldPrice = currentPrice * Math.pow(1.1, year);
            const newGoldThisYear = yearlyAmount / projectedGoldPrice;
            goldAccumulated += newGoldThisYear;
            const totalValue = goldAccumulated * projectedGoldPrice;
    
            projectedData.push({
                year: year + 2024,
                investment: Math.round(totalInvestment),
                value: Math.round(totalValue),
                goldWeight: goldAccumulated,
                monthlyAmount: Math.round(currentMonthly),
                goldPrice: Math.round(projectedGoldPrice)
            });
        }
        return projectedData;
    };

    const projectionData = generateProjectionData();
    const finalYear = projectionData[projectionData.length - 1];

    return (
        <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
            <h3 className="text-lg font-bold text-[#234363] mb-6">توقعات تحويشتك خلال ال 10 سنين الجاية</h3>

            {/* Projection Chart */}
            <div className="h-72 mb-6">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart 
                        data={projectionData}
                        margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis 
                            dataKey="year"
                            stroke="#9CA3AF"
                            tick={{ fill: '#6B7280', fontSize: 12 }}
                        />
                        <YAxis 
                            stroke="#9CA3AF"
                            tick={{ fill: '#6B7280', fontSize: 12 }}
                            tickFormatter={(value) => `${(value/1000).toFixed(0)}K`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Line 
                            type="monotone" 
                            dataKey="investment"
                            name="الاستثمار"
                            stroke="#234363" 
                            strokeWidth={2}
                            dot={false}
                        />
                        <Line 
                            type="monotone" 
                            dataKey="value"
                            name="القيمة"
                            stroke="#FFBE2E" 
                            strokeWidth={2}
                            dot={false}
                        />
                        <Line 
                            type="monotone" 
                            dataKey="goldWeight"
                            name="الدهب"
                            stroke="#4B5563" 
                            strokeWidth={2}
                            dot={false}
                            hide
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Projection Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
    <div className="text-center">
        <div className="text-gray-600 mb-2">إجمالي التحويش</div>
        <div className="text-xl font-bold text-[#234363] ltr">
            EGP {finalYear.investment.toLocaleString()}
        </div>
        <div className="text-sm text-gray-600 mt-2">
            يبدأ بـ {monthlyAverage.toLocaleString()} شهرياً
        </div>
        <div className="text-sm text-gray-600">
            مع زيادة 10% سنوياً
        </div>
    </div>
    <div className="text-center">
        <div className="text-gray-600 mb-2">القيمة المتوقعة للتحويش</div>
        <div className="text-xl font-bold text-[#234363] ltr">
            EGP {finalYear.value.toLocaleString()}
        </div>
        <div className="text-sm text-gray-600 mt-2">
            متوقع زيادة 10% سنوية في سعر الدهب
        </div>
        <div className="text-sm text-gray-600">
            من {currentPrice.toLocaleString()} إلى {finalYear.goldPrice.toLocaleString()} جنيه/جرام
        </div>
    </div>
    <div className="text-center">
        <div className="text-gray-600 mb-2">كمية الدهب</div>
        <div className="text-xl font-bold text-[#234363] ltr">
            {finalYear.goldWeight.toFixed(3)} g
        </div>
        <div className="text-sm text-gray-600 mt-2">
            في السنة العاشرة هتحوش {finalYear.monthlyAmount.toLocaleString()} شهرياً
        </div>
    </div>
</div>

            {/* Success Story */}
            <div className="mt-6 pt-6 border-t">
                <div className="text-sm text-gray-600">
                    <p className="mb-3">
                    قصة حقيقية: عميل بدأ تحويش في 2000 بمتوسط 190 جنيه شهرياً
                    </p>
                    <ul className="space-y-2">
                        <li>• إجمالي التحويش: 57,272 جنيه</li>
                        <li>• القيمة الحالية: 854,128 جنيه</li>
                        <li>• معدل العائد: 1,391%</li>
                        <li>• إجمالي الدهب: 201.79 جرام</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default LongTermProjection;